/**
 * Rock fragments after a fracture (M2 plan §6 M2-20): smaller instances of the parent's archetype from an M2-owned
 * `AsteroidKit` (`createAsteroidKit`), one `InstancedMesh` per archetype at LOD `views.fragments.lod`, each carrying
 * the kit's `aSeed` / `aSpin` instanced attributes (ASTEROID_ATTR). A fragment is scaled to its radius and tumbles
 * slowly about a seed axis (the spin is applied in the instance matrix; `aSpin.w` stays 0 so a material that
 * spins by the attribute does not spin it twice). Matrices are in render space (D-011).
 * Fragments cast key-light shadows (they are the rocks the player mines up close) and are frustum-culled by an
 * instance bounding sphere that `frameFragments` rewrites in place after every pool write, so a shadow level or the
 * main view skips a pool whose fragments are out of its range (PERF PF-4, D-771).
 * Building allocates (once); `frameFragments` is allocation-free (D-014).
 */
import { DynamicDrawUsage, Group, InstancedBufferAttribute, InstancedMesh, type Object3D } from 'three/webgpu'
import type { Quat, Vec3 } from '../../core/math'
import { FIELD_ARCHETYPES } from '../../data/schemas'
import { ASTEROID_ATTR } from '../../render/contracts'
import { enableShadows, type AsteroidKit } from '../../render/environment'
import type { FragmentPool } from '../../space/contracts'
import type { ViewsConfig } from '../spacefx/config'
import { seedRange, seedSpin, writeTRS } from './transform'

export interface FragmentView {
  readonly root: Group
  /** Per layout archetype (FIELD_ARCHETYPES order). */
  readonly meshes: readonly InstancedMesh[]
  readonly seeds: readonly Float32Array[]
  readonly spins: readonly Float32Array[]
  /** Instances per archetype drawn last frame. */
  readonly counts: Int32Array
  readonly capacity: number
  drawn: number
}

export function createFragmentView(kit: AsteroidKit, capacity: number, cfg: ViewsConfig, parent: Object3D): FragmentView {
  const cap = Math.max(1, capacity)
  const root = new Group()
  root.name = 'm2:fragments'
  root.matrixAutoUpdate = false
  const meshes: InstancedMesh[] = []
  const seeds: Float32Array[] = []
  const spins: Float32Array[] = []
  for (let a = 0; a < FIELD_ARCHETYPES.length; a++) {
    const k = kit.archetypes.indexOf(FIELD_ARCHETYPES[a])
    if (k < 0) throw new Error(`fragments: the AsteroidKit has no archetype ${FIELD_ARCHETYPES[a]}`)
    const levels = kit.geometries[k]
    const lod = cfg.fragments.lod < levels.length ? cfg.fragments.lod : levels.length - 1
    const geometry = levels[lod].clone()
    const s = new Float32Array(cap)
    const sp = new Float32Array(cap * 4)
    const seedAttr = new InstancedBufferAttribute(s, 1)
    const spinAttr = new InstancedBufferAttribute(sp, 4)
    seedAttr.setUsage(DynamicDrawUsage)
    spinAttr.setUsage(DynamicDrawUsage)
    geometry.setAttribute(ASTEROID_ATTR.seed, seedAttr)
    geometry.setAttribute(ASTEROID_ATTR.spin, spinAttr)
    const mesh = new InstancedMesh(geometry, kit.materials[k], cap)
    mesh.name = `m2:fragments:${FIELD_ARCHETYPES[a]}`
    mesh.instanceMatrix.setUsage(DynamicDrawUsage)
    mesh.count = 0
    mesh.visible = false
    mesh.matrixAutoUpdate = false
    // Preallocates mesh.boundingSphere (and the geometry's), which frameFragments then rewrites in place.
    mesh.computeBoundingSphere()
    root.add(mesh)
    meshes.push(mesh)
    seeds.push(s)
    spins.push(sp)
  }
  enableShadows(root)
  parent.add(root)
  return { root, meshes, seeds, spins, counts: new Int32Array(FIELD_ARCHETYPES.length), capacity: cap, drawn: 0 }
}

const q: Quat = { x: 0, y: 0, z: 0, w: 1 }

/** Writes every live fragment into its archetype's mesh at render time `time`. Allocation-free. */
export function frameFragments(view: FragmentView, frags: FragmentPool, cfg: ViewsConfig, time: number, cam: Vec3): void {
  const counts = view.counts
  counts.fill(0)
  const archetypes = view.meshes.length
  const high = frags.high < frags.capacity ? frags.high : frags.capacity
  let drawn = 0
  for (let i = 0; i < high; i++) {
    if (frags.alive[i] !== 1) continue
    const a = frags.archetype[i]
    if (a >= archetypes) continue
    const n = counts[a]
    if (n >= view.capacity) continue
    const mesh = view.meshes[a]
    const seed = frags.seed[i]
    const r = frags.radius[i]
    seedSpin(seed, time * seedRange(seed, cfg.fragments.spinRate[0], cfg.fragments.spinRate[1]), q)
    writeTRS(
      mesh.instanceMatrix.array as Float32Array,
      n * 16,
      Math.fround(frags.x[i] - cam.x),
      Math.fround(frags.y[i] - cam.y),
      Math.fround(frags.z[i] - cam.z),
      q.x,
      q.y,
      q.z,
      q.w,
      r,
      r,
      r,
    )
    view.seeds[a][n] = seed
    const sp = view.spins[a]
    sp[n * 4] = 0
    sp[n * 4 + 1] = 1
    sp[n * 4 + 2] = 0
    sp[n * 4 + 3] = 0
    counts[a] = n + 1
    drawn++
  }
  for (let a = 0; a < archetypes; a++) {
    const mesh = view.meshes[a]
    const n = counts[a]
    mesh.count = n
    mesh.visible = n > 0
    if (n === 0) continue
    mesh.instanceMatrix.needsUpdate = true
    // Bounds over the live instances, into the preallocated sphere (three's union uses module scratch: no allocation).
    mesh.computeBoundingSphere()
    const g = mesh.geometry
    const seedAttr = g.getAttribute(ASTEROID_ATTR.seed) as InstancedBufferAttribute
    const spinAttr = g.getAttribute(ASTEROID_ATTR.spin) as InstancedBufferAttribute
    seedAttr.needsUpdate = true
    spinAttr.needsUpdate = true
  }
  view.drawn = drawn
}

export function disposeFragmentView(view: FragmentView): void {
  view.root.removeFromParent()
  for (let a = 0; a < view.meshes.length; a++) {
    view.meshes[a].geometry.dispose()
    view.meshes[a].dispose()
  }
}
